import 'Common/polyfills';
import {
  DARK_MODE_FLAG_CACHE_KEY,
  initTheme,
  THEME_AUTO_DARK_POPUP_SEEN_KEY,
  THEME_SYSTEM_DARK_SEED_KEY,
  USER_THEME_KEY
} from 'Utils/theme';
import 'Styles/index.scss';
import React from 'react';
import { Provider } from 'react-redux';
import { hydrate } from 'react-dom';
import { Router } from 'react-router-dom';
import { loadableReady } from '@loadable/component';
import { LastLocationProvider } from 'react-router-last-location';
import store from 'Store/clientStore';
import { toast, ToastContainer } from 'react-toastify';
import authInterceptor from 'Utils/authentication/authInterceptorClient';
import { handleAxiosErrors } from 'Utils/errorHandler';
import App from 'Components/App';
import ScrollToTop from 'Components/ScrollToTop';
import History from 'Store/history';
import LocalStorage from 'Utils/localStorage';
import { initSentrySdk } from 'Utils/thirdParty/initHelpers';
import MixpanelProvider from 'Components/MixpanelProvider';
import ProjectConfig from 'ProjectConfig';
// import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { updateMbsBackgroundColors } from '../utils/updateMbsBackgroundColors';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from '../common/store/clientStore';
import initFirebase from 'Utils/firebase';
import { initEmbeddedShellFromWindow } from 'Utils/webviewMode';

const isEmbeddedShellClient
  = typeof window !== 'undefined' && initEmbeddedShellFromWindow();

const runWhenIdle = (callback, timeout = 5000) => {
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 1);
  }
};

function scheduleDeferredSentryInit () {
  if (!ProjectConfig.sentryEnabled) return;

  const start = () => {
    runWhenIdle(() => {
      initSentrySdk();
    }, isEmbeddedShellClient ? 7000 : 4000);
  };

  if (!isEmbeddedShellClient) {
    initSentrySdk();
    return;
  }

  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    start();
  } else if (typeof window !== 'undefined') {
    window.addEventListener('load', start, { once: true });
  } else {
    start();
  }
}
window.API_GATEWAY = ProjectConfig.API_GATEWAY;

initTheme();

handleAxiosErrors();
authInterceptor();
if (!isEmbeddedShellClient) {
  checkLocalStorage();
}
updateMbsBackgroundColors();
scheduleDeferredSentryInit();

function scheduleDeferredFirebaseInit () {
  let finished = false;
  const maxAttempts = 200;
  let attempts = 0;

  const tryInit = () => {
    if (finished) return;
    attempts++;
    try {
      if (typeof window !== 'undefined' && window.firebase && typeof window.firebase.initializeApp === 'function') {
        initFirebase();
        finished = true;
        return;
      }
    } catch (err) {
      console.error('Bootstrap error', err);
    }
    if (!finished && attempts < maxAttempts) {
      setTimeout(tryInit, 50);
    }
  };

  const start = () => {
    runWhenIdle(() => {
      if (isEmbeddedShellClient) {
        setTimeout(tryInit, 1000);
      } else {
        tryInit();
      }
    }, isEmbeddedShellClient ? 7000 : 5000);
  };

  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    start();
  } else if (typeof window !== 'undefined') {
    window.addEventListener('load', start);
  } else {
    start();
  }
}

startApp();
scheduleDeferredFirebaseInit();

function startApp () {
  loadableReady(() => {
    const root = document.getElementById('root');

    hydrate(
      <Provider store={ store }>
        <PersistGate loading={ null } persistor={ persistor }>
          <Router history={ History }>
            { /*
              // reCAPTCHA v3 (temporarily disabled)
              // <GoogleReCaptchaProvider reCaptchaKey={ ProjectConfig.GOOGLE_RECAPTCHA }>
              //   <MixpanelProvider>
              //     <LastLocationProvider watchOnlyPathname>
              //       <ScrollToTop>
              //         <App/>
              //         <ToastContainer
              //           position={ toast.POSITION.TOP_CENTER }
              //           autoClose={ 5000 }
              //           hideProgressBar
              //         />
              //       </ScrollToTop>
              //     </LastLocationProvider>
              //   </MixpanelProvider>
              // </GoogleReCaptchaProvider>
            */ }
            <MixpanelProvider>
              <LastLocationProvider watchOnlyPathname>
                <ScrollToTop>
                  <App/>
                  <ToastContainer
                    position={ toast.POSITION.TOP_CENTER }
                    autoClose={ 5000 }
                    hideProgressBar
                  />
                </ScrollToTop>
              </LastLocationProvider>
            </MixpanelProvider>
          </Router>
        </PersistGate>
      </Provider>,
      root
    );
  });
}

const appHeight = () => {
  const doc = document.documentElement;
  doc.style.setProperty('--app-height', `${window.innerHeight}px`);
};
window.addEventListener('resize', appHeight);
appHeight();

function checkLocalStorage () {
  const lastVersion = LocalStorage.get('version');
  const currentVersion = ProjectConfig.version;

  if (lastVersion !== currentVersion) {
    const preservedTheme = localStorage.getItem(USER_THEME_KEY);
    const preservedThemeFlag = localStorage.getItem(DARK_MODE_FLAG_CACHE_KEY);
    const preservedAutoDarkPopupSeen = localStorage.getItem(THEME_AUTO_DARK_POPUP_SEEN_KEY);
    const preservedSystemDarkSeed = localStorage.getItem(THEME_SYSTEM_DARK_SEED_KEY);

    LocalStorage.clear();

    if (preservedTheme) {
      localStorage.setItem(USER_THEME_KEY, preservedTheme);
    }
    if (preservedThemeFlag) {
      localStorage.setItem(DARK_MODE_FLAG_CACHE_KEY, preservedThemeFlag);
    }
    if (preservedAutoDarkPopupSeen) {
      localStorage.setItem(THEME_AUTO_DARK_POPUP_SEEN_KEY, preservedAutoDarkPopupSeen);
    }
    if (preservedSystemDarkSeed) {
      localStorage.setItem(THEME_SYSTEM_DARK_SEED_KEY, preservedSystemDarkSeed);
    }
    LocalStorage.set('version', currentVersion);
  }
}
